/*
 * Copyright (c) 2026 Bluemate B.V.
 *
 * React hook that wraps the browser's push subscription API and keeps the
 * backend device-token table in sync. Component UI just calls
 * enable/disable/refresh; all the Notification/PushManager/ServiceWorker
 * plumbing lives here.
 */

import {useCallback, useEffect, useState} from 'react';

import {getSetting, ResponseData} from '@dvrd/dvr-controls';

import {reportError} from '../util/sentryUtil';

import {listDeviceTokens, registerDeviceToken, unregisterDeviceToken} from '../util/requestSender';

import {arrayBufferToBase64Url, urlBase64ToUint8Array} from '../util/pushCrypto';

import {awaitActiveServiceWorker, getDeviceLabel, isMobileDevice, isStandalone} from '../util/pwaUtil';

import {PushSubscriptionState} from '../util/interfaces';

import {createSnooze} from '../util/snoozeUtil';

import {
    addOptedInPair as idbAddOptedInPair,
    removeOptedInPair as idbRemoveOptedInPair,
    setUserOptedIn as idbSetUserOptedIn,
    getLastEndpoint as idbGetLastEndpoint,
    setLastEndpoint as idbSetLastEndpoint
} from '../util/pushIdb';

export type {PushSubscriptionState};

// Per-(user, account) opt-in flag in localStorage.
//
// Multi-user architecture (1 SW, N users) is preserved verbatim — a single
// browser subscription is shared by every user that has ever logged in on
// this device and each user opts in/out independently.
//
// On top of that we layer per-sales-account opt-in: a user with multiple
// sales accounts can enable push for some and not others on the same
// browser. Key format: `bluemate-push-opted-in:<userId>:<accountId>`.
//
// No legacy-key migration: an earlier version of the code migrated the
// pre-account key `bluemate-push-opted-in:<userId>` and the pre-multi-user
// global key on first read, but that migrated the flag onto whatever
// account the user happened to be viewing at that moment — switching to
// a different account then "discovered" the same flag through the
// migration path and flipped that account's toggle ON without consent.
// Clean break is the simpler invariant: after the deploy, users opt in
// once per (user, account) pair they actually want push for.
// Error codes with dedicated push.errors.* translations in the settings card.
const KNOWN_PUSH_ERRORS = new Set([
    'unsupported',
    'missing-vapid',
    'missing-keys',
    'install-required',
    'sw-not-ready',
    'enable-failed',
    'disable-failed',
    'register-failed',
    'no-user',
    'prompt-suppressed'
]);

const PUSH_OPTED_IN_PREFIX = 'bluemate-push-opted-in:';
// Dispatched on window whenever this device's push state changes from OUTSIDE
// the notifications page. The /notificaties view listens and refreshes the
// status card + the device list so both reflect the change ("Refresh status
// and devices").
export const PWA_PUSH_DEVICES_REFRESH_EVENT = 'pwa:push-devices-refresh';

// True while enable() is driving the native permission prompt + subscribe. The
// visibilitychange→refresh() handlers (in this hook AND on the notifications
// page) skip while it's set, so the OS dialog appearing/closing never kicks off
// a refresh cascade that competes with the prompt for the main thread.
let pushPromptInFlight = false;
export function isPushPromptInFlight(): boolean {
    return pushPromptInFlight;
}

export interface PushHealth {
    ok: boolean;
    /** keys of the checks that FAILED (empty when ok) */
    failed: string[];
}

/**
 * Config-level push health check — verifies everything the SW needs to RECEIVE
 * and show notifications, WITHOUT sending one. It cannot prove end-to-end
 * delivery (an empty subscription, an OS-level notification block, or an FCM
 * drop only surface on a real send), but it catches the common "it won't work"
 * misconfigurations: no https, no permission, dead SW, a missing/keyless/expired
 * subscription, or one the backend doesn't know about.
 */
export async function runPushHealthCheck(accountId: string | null, vapid: string): Promise<PushHealth> {
    const checks: Record<string, boolean> = {
        secureContext: typeof window !== 'undefined' && window.isSecureContext === true,
        supported:
            typeof navigator !== 'undefined' &&
            'serviceWorker' in navigator &&
            typeof PushManager !== 'undefined' &&
            typeof Notification !== 'undefined',
        permission: typeof Notification !== 'undefined' && Notification.permission === 'granted',
        swActive: false,
        // Assumed ok; flipped to false only on a DEFINITIVE non-granted result,
        // so browsers without pushManager.permissionState() don't false-fail.
        pushPermission: true,
        subscription: false,
        keys: false,
        notExpired: true,
        backendToken: false
    };
    let endpoint: string | null = null;
    try {
        const reg = (await navigator.serviceWorker?.getRegistration('/')) ?? null;
        checks.swActive = !!reg?.active;
        if (reg) {
            if (vapid) {
                try {
                    const state = await reg.pushManager.permissionState({
                        userVisibleOnly: true,
                        applicationServerKey: urlBase64ToUint8Array(vapid)
                    });
                    checks.pushPermission = state === 'granted';
                } catch {
                    /* permissionState unsupported — leave assumed-ok */
                }
            }
            const sub = await reg.pushManager.getSubscription();
            checks.subscription = !!sub;
            if (sub) {
                endpoint = sub.endpoint;
                checks.keys = !!(sub.getKey('p256dh') && sub.getKey('auth'));
                checks.notExpired = !sub.expirationTime || sub.expirationTime > Date.now();
            }
        }
    } catch {
        /* SW/registration failure already reflected by the false defaults */
    }
    // Backend knows this exact endpoint as an ACTIVE token (read-only, no send).
    if (endpoint && accountId) {
        try {
            checks.backendToken = await new Promise<boolean>(resolve => {
                listDeviceTokens({
                    account_id: accountId,
                    callback: (data: ResponseData) => {
                        const rows = (data.tokens || data.device_tokens || []) as Array<{
                            endpoint?: string | null;
                            is_active?: boolean;
                        }>;
                        resolve(rows.some(r => r.endpoint === endpoint && r.is_active !== false));
                    },
                    // Network failure / non-2xx never fires `callback` — resolve
                    // false so the awaiting toggle flow never hangs.
                    errorCallback: () => resolve(false)
                });
            });
        } catch {
            /* network — leave false */
        }
    }
    const failed = Object.keys(checks).filter(k => !checks[k]);
    return {ok: failed.length === 0, failed};
}

// Schema version sentinel — bumped whenever the localStorage key format
// changes. On detect-mismatch we wipe every push opt-in key and set the
// sentinel; users re-enable the (user, account) pairs they actually want.
// This is the one-time cleanup path for users who accumulated stale flags
// during the per-user → per-account migration window.
const PUSH_SCHEMA_VERSION_KEY = 'bluemate-push-schema-version';
const PUSH_SCHEMA_VERSION = '2';

// Per-(user, account) native-prompt back-off.
//
// The browser permission is per-ORIGIN and Chrome auto-blocks it after a few
// dismissed/ignored prompts WITHOUT the user ever choosing "Block": the loud
// modal embargoes after 3 dismissals / 4 ignores, but the QUIET permission UI
// (a tiny chip that's easy to miss, which Android frequently uses) embargoes
// after as little as ONE dismissal / TWO ignores. So if we re-fire
// requestPermission() on every toggle tap, a user who taps a few times — each
// time dismissing or missing the chip — drives the origin straight into a
// 7-day embargo, after which the prompt is silently denied. That is the
// reported "tapped 4×, nothing happened, Chrome blocked notifications" bug.
//
// Fix: after a single 'default' (dismissed/ignored/suppressed) result we stop
// calling requestPermission() and route the user to the in-app guide instead.
// THRESHOLD is 1 to stay strictly below the quiet-UI embargo line. The window
// is time-bound (mirrors Chrome's own 7-day embargo) so an accidental single
// dismissal self-heals rather than permanently dead-ending the toggle.
//
// Prefix is deliberately NOT a string-prefix of PUSH_OPTED_IN_PREFIX so the
// opt-in enumerations (startsWith(PUSH_OPTED_IN_PREFIX + ...)) never match it.
const PUSH_PROMPT_DISMISS_PREFIX = 'bluemate-push-dismiss:';
const PUSH_PROMPT_BACKOFF_MS = 7 * 24 * 60 * 60 * 1000; // 7 days, mirrors Chrome's embargo

(function migratePushSchema() {
    try {
        if (typeof localStorage === 'undefined') return;
        if (localStorage.getItem(PUSH_SCHEMA_VERSION_KEY) === PUSH_SCHEMA_VERSION) return;
        const stale: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            // Sweep BOTH the opt-in flags and the prompt back-off counters on a
            // schema bump so a reset gives a genuinely clean slate (a leftover
            // dismiss counter would otherwise keep suppressing the prompt).
            if (k && (k.startsWith(PUSH_OPTED_IN_PREFIX) || k.startsWith(PUSH_PROMPT_DISMISS_PREFIX))) stale.push(k);
        }
        for (const k of stale) localStorage.removeItem(k);
        localStorage.setItem(PUSH_SCHEMA_VERSION_KEY, PUSH_SCHEMA_VERSION);
    } catch (_e) {
        /* private browsing */
    }
})();

function getOptedInKey(userId: string, accountId: string): string {
    return `${PUSH_OPTED_IN_PREFIX}${userId}:${accountId}`;
}

export function getOptedIn(userId: string | null, accountId: string | null): boolean {
    if (!userId || !accountId) return false;
    try {
        return localStorage.getItem(getOptedInKey(userId, accountId)) === '1';
    } catch (_e) {
        return false;
    }
}

function setOptedIn(userId: string | null, accountId: string | null, value: boolean): void {
    if (!userId || !accountId) return;
    try {
        if (value) localStorage.setItem(getOptedInKey(userId, accountId), '1');
        else localStorage.removeItem(getOptedInKey(userId, accountId));
    } catch (_e) {
        /* private browsing */
    }
}

/** True while ANY of this user's sales accounts is opted in on this device
 *  — the per-user IDB mirror the SW reads for the multi-user label. */
export function anyAccountOptedIn(userId: string): boolean {
    try {
        return Object.keys(localStorage).some(
            k => k.startsWith(PUSH_OPTED_IN_PREFIX + userId + ':') && localStorage.getItem(k) === '1'
        );
    } catch (_e) {
        return false;
    }
}

function getDismissSnooze(userId: string, accountId: string) {
    return createSnooze(`${PUSH_PROMPT_DISMISS_PREFIX}${userId}:${accountId}`, [PUSH_PROMPT_BACKOFF_MS]);
}

/** Record that the native permission prompt was just dismissed/ignored for
 *  this (user, account). */
function bumpDismissCount(userId: string | null, accountId: string | null): void {
    if (!userId || !accountId) return;
    getDismissSnooze(userId, accountId).recordDismissal();
}

/** Clear the back-off for this (user, account) — called on a successful grant
 *  so a later disable → re-enable starts fresh. */
function resetDismissCount(userId: string | null, accountId: string | null): void {
    if (!userId || !accountId) return;
    getDismissSnooze(userId, accountId).reset();
}

/** True when we should NOT fire the native prompt for this (user, account):
 *  it was dismissed/ignored and the most recent dismissal is within the
 *  back-off window. Outside the window the back-off self-heals (we allow one
 *  more genuine prompt), mirroring Chrome's own 7-day embargo expiry. */
function isPromptSuppressed(userId: string | null, accountId: string | null): boolean {
    if (!userId || !accountId) return false;
    // Permission already granted → there is nothing to prompt for, so NEVER
    // suppress. Without this, a dismissal counter racked up while permission was
    // 'default' kept blocking the user (the "we won't show the prompt again"
    // guide) even after they granted notifications from the browser settings —
    // the reported bug. The back-off only exists to avoid Chrome's prompt embargo
    // while permission is still pending; once granted it is irrelevant.
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') return false;
    // Never suppress inside an installed PWA. The back-off exists only to stop a
    // BROWSER TAB from driving the origin into Chrome's permission embargo via
    // repeated prompts; an installed app is a high-intent context and must always
    // be able to ask. Critical on Android, where the installed PWA shares the
    // origin's localStorage with Chrome — so a dismissal counter racked up in the
    // browser (e.g. Chrome blocked notifications there) would otherwise leak in
    // and permanently block enabling push from inside the PWA. If the origin is
    // genuinely 'denied', enable() still catches that and routes to the settings
    // guide; here we only ensure the user can always attempt to enable.
    if (isStandalone()) return false;
    return getDismissSnooze(userId, accountId).isSnoozed();
}

// enable() resolves to 'device-limit' when the backend rejected the device
// because the (user, account) is at its cap — the caller surfaces that as a
// toast. Any other outcome (success or an inline-hint error) resolves to void.
type EnableResult = 'device-limit' | void;

interface UsePushSubscription {
    state: PushSubscriptionState;
    enable: () => Promise<EnableResult>;
    disable: () => Promise<void>;
    refresh: () => Promise<void>;
}

function detectSupport(): boolean {
    if (typeof window === 'undefined') return false;
    if (!('serviceWorker' in navigator)) return false;
    if (!('PushManager' in window)) return false;
    if (!('Notification' in window)) return false;
    return true;
}

function initialPermission(): NotificationPermission {
    if (typeof Notification === 'undefined') return 'default';
    return Notification.permission;
}

/**
 * Resolve a ServiceWorkerRegistration with an ACTIVE worker, bounded by `ms`,
 * so the UI never hangs waiting for the SW.
 *
 * Delegates to awaitActiveServiceWorker (pwaUtil), which is robust on iOS
 * standalone PWAs where navigator.serviceWorker.ready is known to hang: it
 * checks getRegistration().active directly, (re)registers /sw.js if none exists
 * yet (e.g. the toggle was tapped before registerServiceWorker() finished) and
 * waits on `statechange` for activation, only falling back to .ready last. Still
 * rejects with `sw-not-ready` on timeout so enable()/disable() never hang.
 */
function swReadyWithTimeout(ms: number = 15000): Promise<ServiceWorkerRegistration> {
    return awaitActiveServiceWorker(ms);
}

export function usePushSubscription(userId: string | null, accountId: string | null): UsePushSubscription {
    const standalone = isStandalone();
    // Mobile requires the INSTALLED PWA for push. On iOS that is a platform
    // constraint (PushManager only exists in the home-screen app; Safari
    // < 16.4 doesn't ship it at all — detectSupport() catches that). On
    // Android it is a product decision: a browser-tab subscription living
    // next to the installed app's is exactly what produced duplicate
    // notifications, so the browser context may not enable push at all.
    // Desktop browsers are unaffected.
    //
    // isMobileDevice(), NOT isAndroid()/isIOS(): the browser's desktop-site
    // toggle rewrites the user-agent, and a UA-only check handed a phone the
    // desktop rules the moment the user flipped it.
    const mobile = isMobileDevice();
    const canUseHere = !mobile || standalone;

    const [state, setState] = useState<PushSubscriptionState>({
        permission: initialPermission(),
        subscription: null,
        // Initial optedIn read from localStorage (synchronous) so the very
        // first render of the toggle already reflects the per-(user, account)
        // intent. Without this the toggle would flash through "OFF" while the
        // initial refresh resolves, regardless of stored flag.
        optedIn: getOptedIn(userId, accountId),
        isSupported: detectSupport(),
        isSubscribing: false,
        isStandalone: standalone,
        canUseHere,
        promptSuppressed: isPromptSuppressed(userId, accountId),
        error: null
    });

    /** Read the current subscription (if any) from the active SW, and keep
     *  it in sync with the backend device-token table. If the browser still
     *  has a subscription but the backend has a positive "no active token
     *  for this endpoint" response (e.g. the user just logged this device
     *  out via DeviceList), we revoke the browser subscription so the UI
     *  drops back to "off". We only auto-revoke on a confirmed backend
     *  negative — if the listDeviceTokens call itself fails (network error,
     *  500) we leave the browser subscription alone to avoid silently
     *  killing push whenever the API is briefly unreachable. */
    const refresh = useCallback(async () => {
        if (!detectSupport()) return;
        try {
            const reg = await swReadyWithTimeout();
            // The push subscription is per-browser and shared across every
            // (user, account) pair. Read it ONCE — getSubscription() is an async
            // round-trip to the push service — and reuse it for every check below.
            const browserSub = await reg.pushManager.getSubscription();
            let sub: PushSubscription | null = browserSub;

            // Always fetch backend state for the CURRENT (user, account)
            // pair so we can make informed decisions about the toggle and
            // the auto-resubscribe path.
            const backendTokens = await new Promise<Array<{endpoint?: string | null; is_active?: boolean}> | null>(
                resolve => {
                    if (!accountId) {
                        resolve(null);
                        return;
                    }
                    listDeviceTokens({
                        account_id: accountId,
                        callback: (data: ResponseData) => {
                            if (!data.success) return resolve(null);
                            resolve(data.tokens || data.device_tokens || []);
                        },
                        // Network failure / non-2xx never fires `callback` —
                        // without this the await above would hang forever.
                        errorCallback: () => resolve(null)
                    });
                }
            );

            const optedIn = getOptedIn(userId, accountId);

            // Keep the SW's opted-in (user, account) pair mirror in sync with
            // localStorage on every refresh. Seeds pairs for users who opted
            // in before this mirror existed, and prunes stale ones — so the
            // pushsubscriptionchange re-register has an account to target.
            if (userId && accountId) {
                if (optedIn) void idbAddOptedInPair(userId, accountId);
                else void idbRemoveOptedInPair(userId, accountId);
                // Keep the per-user opted_in_users mirror current too — it is the
                // only signal the SW uses for the multi-user notification label.
                // Recompute across ALL of this user's accounts so the flag means
                // "this user wants push on at least one account on this device".
                void idbSetUserOptedIn(userId, anyAccountOptedIn(userId));
            }

            // Multi-user invariant (unchanged) + per-account layer: the
            // browser subscription is shared across every (user, account)
            // tuple on this device. We only EXPOSE it via state.subscription
            // if the CURRENT (user, account) pair has opted in — toggle
            // reads OFF when the same browser subscription is alive for a
            // different user or a different sales account.
            if (sub && !optedIn) {
                sub = null;
            }

            // Phantom-row cleanup: localStorage says OFF but backend has an
            // active DeviceToken row for THIS browser endpoint under the
            // CURRENT (user, account) pair. This can happen for users who
            // accumulated rows during the per-user → per-account migration
            // window. Source of truth is localStorage (user intent) — sync
            // the backend by issuing an unregister so the device list and
            // the toggle agree and a future push for this account does not
            // surprise the user.
            if (browserSub && backendTokens !== null && !optedIn && accountId) {
                const phantom = backendTokens.find(r => r.is_active !== false && r.endpoint === browserSub.endpoint);
                if (phantom) {
                    await new Promise<void>(resolve => {
                        unregisterDeviceToken({
                            data: {account_id: accountId, endpoint: browserSub.endpoint},
                            callback: () => resolve(),
                            // Network failure / non-2xx never fires `callback` —
                            // settle so refresh() cannot hang (best-effort cleanup).
                            errorCallback: () => resolve()
                        });
                    });
                }
            }

            // If current (user, account) opted in but backend lost their
            // row for this endpoint (e.g. admin just impersonated, or user
            // just switched to a sales account that never had a row),
            // repair silently. Rows for other (user, account) pairs on
            // the same endpoint are untouched. NOT in a mobile browser tab:
            // on phones only the installed app may (re)register, so labels
            // never downgrade from "(installed app)" to a browser row and a
            // zombie left by an uninstall is never re-registered.
            if (sub && browserSub && backendTokens !== null && optedIn && accountId && canUseHere) {
                const match = backendTokens.some(r => r.is_active !== false && r.endpoint === browserSub.endpoint);
                if (!match) {
                    const p256dh = arrayBufferToBase64Url(browserSub.getKey('p256dh'));
                    const auth = arrayBufferToBase64Url(browserSub.getKey('auth'));
                    if (p256dh && auth) {
                        try {
                            const platformLabel = await getDeviceLabel();
                            const oldEndpoint = userId ? await idbGetLastEndpoint(userId) : null;
                            const previousEndpoint =
                                oldEndpoint && oldEndpoint !== browserSub.endpoint ? oldEndpoint : undefined;
                            await new Promise<void>((resolve, reject) => {
                                registerDeviceToken({
                                    data: {
                                        account_id: accountId,
                                        channel: 'WEB_PUSH',
                                        endpoint: browserSub.endpoint,
                                        p256dh,
                                        auth,
                                        user_agent: navigator.userAgent,
                                        platform: platformLabel,
                                        ...(previousEndpoint ? {previous_endpoint: previousEndpoint} : {})
                                    },
                                    callback: (data: ResponseData) => {
                                        if (data.success) resolve();
                                        else reject(new Error(data.message || 'register-failed'));
                                    },
                                    // Non-2xx / network failure never fires `callback`;
                                    // reject into the surrounding best-effort catch so
                                    // refresh() always settles.
                                    errorCallback: () => reject(new Error('register-failed'))
                                });
                            });
                            if (userId) await idbSetLastEndpoint(userId, browserSub.endpoint);
                        } catch (_err) {
                            /* best effort */
                        }
                    }
                }
            }

            // iOS drops the push subscription when the PWA is force-closed.
            // Auto-resubscribe only if the CURRENT (user, account) pair
            // previously opted in on this device. Without the per-pair flag
            // the next user/account combo would silently inherit the
            // previous one's subscription. NOT in a mobile browser tab —
            // minting a browser subscription there is exactly what the
            // install gate forbids.
            if (!sub && Notification.permission === 'granted' && optedIn && accountId && canUseHere) {
                const vapid = getSetting('vapidPublicKey') as string;
                if (vapid) {
                    try {
                        sub = await reg.pushManager.subscribe({
                            userVisibleOnly: true,
                            applicationServerKey: urlBase64ToUint8Array(vapid)
                        });
                        const p256dh = arrayBufferToBase64Url(sub.getKey('p256dh'));
                        const auth = arrayBufferToBase64Url(sub.getKey('auth'));
                        if (p256dh && auth) {
                            const platformLabel = await getDeviceLabel();
                            // iOS dropped the old subscription, so getSubscription() was
                            // null and the old endpoint is only known from IDB. Pass it as
                            // previous_endpoint so the backend reaps the rotated row instead
                            // of leaving a duplicate "iOS, Safari (PWA)" device behind.
                            const oldEndpoint = userId ? await idbGetLastEndpoint(userId) : null;
                            const previousEndpoint =
                                oldEndpoint && oldEndpoint !== sub!.endpoint ? oldEndpoint : undefined;
                            await new Promise<void>((resolve, reject) => {
                                registerDeviceToken({
                                    data: {
                                        account_id: accountId,
                                        channel: 'WEB_PUSH',
                                        endpoint: sub!.endpoint,
                                        p256dh,
                                        auth,
                                        user_agent: navigator.userAgent,
                                        platform: platformLabel,
                                        ...(previousEndpoint ? {previous_endpoint: previousEndpoint} : {})
                                    },
                                    callback: (data: ResponseData) => {
                                        if (data.success) resolve();
                                        else reject(new Error(data.message || 'auto-resubscribe-failed'));
                                    },
                                    // Non-2xx / network failure never fires `callback`;
                                    // reject into the surrounding best-effort catch so
                                    // refresh() always settles.
                                    errorCallback: () => reject(new Error('auto-resubscribe-failed'))
                                });
                            });
                            if (userId) await idbSetLastEndpoint(userId, sub!.endpoint);
                        } else {
                            try {
                                await sub.unsubscribe();
                            } catch (_err) {
                                /* best effort */
                            }
                            sub = null;
                        }
                    } catch (_err) {
                        sub = null;
                    }
                }
            }

            setState(prev => ({
                ...prev,
                subscription: sub,
                // Mirror localStorage opt-in in state so the toggle never
                // disagrees with the per-(user, account) flag even if some
                // other tab wrote to localStorage between the useEffect read
                // and refresh resolution.
                optedIn,
                permission: Notification.permission,
                error: null
            }));
        } catch (err) {
            const message = (err as Error)?.message ?? String(err);
            // Known, user-actionable codes map to push.errors.* translations in
            // the card. Anything else (e.g. a raw browser SW-registration
            // failure) is an internal error: report it, never show it raw.
            if (!KNOWN_PUSH_ERRORS.has(message)) {
                console.error('[push] setup failed:', err);
                reportError(err instanceof Error ? err : new Error(message));
            }
            setState(prev => ({...prev, error: KNOWN_PUSH_ERRORS.has(message) ? message : 'unexpected'}));
        }
    }, [userId, accountId, canUseHere]);

    useEffect(() => {
        // On (userId, accountId) change, sync the toggle SYNCHRONOUSLY to the
        // new pair's opt-in flag. `state.optedIn` is the single source of
        // truth that PushNotificationsCard reads — avoids any race between
        // the async refresh path and the UI render. localStorage is read
        // synchronously so the toggle never flashes through a stale state
        // from the previous (user, account).
        const optedInForCurrentPair = getOptedIn(userId, accountId);
        setState(prev => ({
            ...prev,
            optedIn: optedInForCurrentPair,
            // Null the browser-side subscription view when the new pair is
            // not opted in. Internal-only — the actual browser subscription
            // stays alive for other (user, account) pairs that share it.
            subscription: optedInForCurrentPair ? prev.subscription : null,
            // Back-off is per-(user, account): recompute for the new pair so
            // switching account doesn't carry over the previous pair's
            // suppressed/normal prompt state.
            promptSuppressed: isPromptSuppressed(userId, accountId),
            isSubscribing: false,
            error: null
        }));
        void refresh();
        let debounceTimer: ReturnType<typeof setTimeout> | undefined;
        function onVisibilityChange() {
            // Skip while a permission prompt is in flight: the OS dialog toggles
            // visibility, and refreshing then both wastes work and competes with
            // the prompt for the main thread.
            if (document.visibilityState !== 'visible' || pushPromptInFlight) return;
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => void refresh(), 2000);
        }
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            clearTimeout(debounceTimer);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [refresh]);

    const enable = useCallback(async () => {
        if (!detectSupport()) {
            setState(prev => ({...prev, error: 'unsupported'}));
            return;
        }
        if (!userId || !accountId) {
            // Cannot opt in without (user, account) context. The backend
            // register call requires account_id, and per-account opt-in
            // flag is what makes the toggle differ between sales accounts.
            setState(prev => ({...prev, error: 'no-user'}));
            return;
        }
        if (mobile && !standalone) {
            setState(prev => ({...prev, error: 'install-required'}));
            return;
        }
        const vapid = getSetting('vapidPublicKey') as string;
        if (!vapid) {
            setState(prev => ({...prev, error: 'missing-vapid'}));
            return;
        }

        // Synchronous, gesture-preserving back-off — runs BEFORE any await and
        // BEFORE flipping isSubscribing, so the early-return paths never touch
        // the spinner and the user-activation gesture stays intact for the
        // requestPermission() call below.
        const currentPermission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
        if (currentPermission === 'denied') {
            // Defense-in-depth: onToggle already answers a denied origin with
            // a toast, but state.permission can be stale (denied in a prior
            // session, refresh() not yet resolved). Never call
            // requestPermission() against a denied origin — it shows no UI and
            // just resolves 'denied'. Surface the denied state so the card
            // toasts on the next tap.
            setState(prev => ({...prev, permission: 'denied', isSubscribing: false, promptSuppressed: false}));
            return;
        }
        // NOTE: we deliberately do NOT pre-block here on a recent dismissal. That
        // back-off (one dismissal → 7 days) trapped users behind the "we won't
        // show the prompt again" guide with no way to retry from the toggle.
        // requestPermission() itself is the right gate: if the browser has gone
        // quiet (its own embargo) it simply resolves 'default' and we show the
        // settings hint as a REACTION — but every tap is free to try again.

        setState(prev => ({...prev, isSubscribing: true, error: null}));
        // Suppress visibilitychange→refresh while the prompt + subscribe run, so
        // the OS dialog opening/closing doesn't trigger a refresh cascade on the
        // main thread (which made the dialog appear frozen/unresponsive).
        pushPromptInFlight = true;
        try {
            let permission: NotificationPermission = currentPermission;
            // Only fire the native prompt when the decision is still pending.
            // If permission is already 'granted' we skip straight to subscribe
            // (re-calling requestPermission() is pointless and was happening on
            // every tap). 'denied' was already handled above.
            if (permission === 'default') {
                // Browsers only allow this from a user gesture, so callers must
                // call enable() from an onClick handler.
                permission = await Notification.requestPermission();
                if (permission !== 'granted') {
                    // 'default' = dismissed/ignored/quiet-chip-missed. Record it
                    // for the dismissal stats. Surface 'prompt-suppressed' ONLY as
                    // a transient reaction to THIS dismissal (cleared by the next
                    // refresh), so the settings hint appears now but never becomes
                    // a persistent block — the toggle stays free to retry. 'denied'
                    // falls through to the card's denied guide (isDenied), no hint.
                    if (permission === 'default') bumpDismissCount(userId, accountId);
                    setState(prev => ({
                        ...prev,
                        permission,
                        isSubscribing: false,
                        promptSuppressed: isPromptSuppressed(userId, accountId),
                        error: permission === 'default' ? 'prompt-suppressed' : null
                    }));
                    return;
                }
            }

            const reg = await swReadyWithTimeout();
            // Reuse existing subscription if the browser already has one —
            // subscribe() is idempotent per (registration, applicationServerKey)
            // but being explicit avoids a re-hit on the push service.
            let sub = await reg.pushManager.getSubscription();
            // Whether the browser already had a subscription (shared across all
            // (user, account) pairs on this browser). Used on a device-cap
            // rejection to decide whether it's safe to unsubscribe.
            const hadExistingSub = !!sub;
            if (!sub) {
                sub = await reg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(vapid)
                });
            }

            const p256dh = arrayBufferToBase64Url(sub.getKey('p256dh'));
            const auth = arrayBufferToBase64Url(sub.getKey('auth'));
            // The browser can hand back an EMPTY / non-functional subscription
            // (no endpoint or no encryption keys) when permission was not really
            // granted — notably the Android WebAPK window right after install,
            // where the PWA transiently reports 'granted' before the OS settles
            // its own permission to 'default'. Registering it would flip the
            // toggle ON but deliver nothing ("empty subscription"). Abort instead:
            // roll the subscription back, surface the state, and DO NOT clear the
            // re-enable flag — so the next attempt (once the permission has
            // settled and the real system dialog can appear) re-subscribes fresh.
            if (!sub.endpoint || !p256dh || !auth) {
                try {
                    await sub.unsubscribe();
                } catch (_err) {
                    /* best effort */
                }
                setState(prev => ({...prev, isSubscribing: false, error: 'empty-subscription'}));
                return;
            }

            const platformLabel = await getDeviceLabel();
            const oldEndpoint = userId ? await idbGetLastEndpoint(userId) : null;
            const previousEndpoint = oldEndpoint && oldEndpoint !== sub!.endpoint ? oldEndpoint : undefined;
            try {
                await new Promise<void>((resolve, reject) => {
                    registerDeviceToken({
                        data: {
                            account_id: accountId,
                            channel: 'WEB_PUSH',
                            endpoint: sub!.endpoint,
                            p256dh,
                            auth,
                            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
                            platform: platformLabel,
                            ...(previousEndpoint ? {previous_endpoint: previousEndpoint} : {})
                        },
                        callback: (data: ResponseData) => {
                            if (data.success) return resolve();
                            // Map the backend device-cap rejection to a dedicated
                            // code the toggle handler surfaces as a toast.
                            const code = (data as {error_code?: string}).error_code;
                            reject(
                                new Error(
                                    code === 'device_limit_reached' ? 'device-limit' : data.message || 'register-failed'
                                )
                            );
                        },
                        // Non-2xx / network failure never fires `callback`. Without
                        // this the Promise never settles: isSubscribing stays true
                        // AND the module-level pushPromptInFlight flag is never
                        // cleared by the finally below, suppressing every later
                        // visibilitychange refresh for the rest of the session.
                        errorCallback: () => reject(new Error('register-failed'))
                    });
                });
                if (userId) await idbSetLastEndpoint(userId, sub!.endpoint);
            } catch (err) {
                // Backend registration failed — revoke the browser-side
                // subscription so we don't leave the user with push enabled
                // in the browser but no backend knowledge of the endpoint
                // (silent non-delivery + an orphan sub that survives restart).
                // EXCEPT at the device cap when the sub ALREADY existed: it is
                // shared across the (user, account) pairs on this browser, so
                // unsubscribing would silence the others — and the cap is a
                // policy limit, not a broken sub. A sub freshly minted for THIS
                // attempt is still revoked (it would otherwise be a true orphan).
                const isLimit = (err as Error)?.message === 'device-limit';
                if (!(isLimit && hadExistingSub)) {
                    try {
                        await sub.unsubscribe();
                    } catch (_err) {
                        /* best effort */
                    }
                }
                throw err;
            }

            setOptedIn(userId, accountId, true);
            // Refresh the status card + device list on /notificaties.
            // (Same-tab → no storage event, so we dispatch our own.)
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event(PWA_PUSH_DEVICES_REFRESH_EVENT));
            }
            // Success — clear any prompt back-off so a later disable → re-enable
            // starts with a fresh prompt budget for this (user, account).
            resetDismissCount(userId, accountId);
            // Mirror per-user opted-in to IDB so the service worker can
            // read it (localStorage is inaccessible from SW) for the
            // multi-user account label decision. Keyed by user only —
            // multi-user logic is preserved verbatim and unaffected by
            // the per-account dimension layered above.
            void idbSetUserOptedIn(userId, true);
            // Also mirror the (user, account) pair so the SW can re-register
            // this endpoint under the right account on pushsubscriptionchange.
            void idbAddOptedInPair(userId, accountId);
            setState(prev => ({
                ...prev,
                subscription: sub!,
                optedIn: true,
                permission,
                isSubscribing: false,
                promptSuppressed: false,
                error: null
            }));
        } catch (err) {
            const message = (err as Error)?.message || 'enable-failed';
            // Device cap reached: the toggle handler shows a toast ("remove a
            // device"), so keep it OUT of the inline error hint and off Sentry,
            // and return the code so the caller can react.
            if (message === 'device-limit') {
                setState(prev => ({...prev, isSubscribing: false, error: null}));
                return 'device-limit';
            }
            // Same policy as refresh(): known, user-actionable codes render
            // their push.errors.* translation; anything else is an internal
            // error — report it to Sentry, never show it raw.
            if (!KNOWN_PUSH_ERRORS.has(message)) {
                console.error('[push] enable failed:', err);
                reportError(err instanceof Error ? err : new Error(message));
            }
            setState(prev => ({
                ...prev,
                isSubscribing: false,
                error: KNOWN_PUSH_ERRORS.has(message) ? message : 'unexpected'
            }));
        } finally {
            // Cleared regardless of how the try exited (grant, early return on a
            // non-grant, missing-keys, throw) so the guard never sticks on.
            pushPromptInFlight = false;
        }
    }, [userId, accountId, mobile, standalone]);

    const disable = useCallback(async () => {
        if (!detectSupport()) return;
        if (!userId || !accountId) return;
        setState(prev => ({...prev, isSubscribing: true, error: null}));
        try {
            const reg = await swReadyWithTimeout();
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                // Backend cleanup: only the CURRENT (user, account) row
                // for this endpoint is deactivated. Other (user, account)
                // pairs on this device keep their rows alive — they may
                // still want pushes here (multi-user invariant +
                // per-account dimension).
                await new Promise<void>(resolve => {
                    unregisterDeviceToken({
                        data: {account_id: accountId, endpoint: sub.endpoint},
                        callback: () => resolve(),
                        // Non-2xx / network failure never fires `callback`. Treat
                        // backend cleanup as best-effort and settle so the local
                        // opt-out below still runs (a stuck toggle is worse than a
                        // lingering backend row the next refresh reconciles) —
                        // matching pushSubscriptionGuard's handling of this call.
                        errorCallback: () => resolve()
                    });
                });
            }
            // CRITICAL: do NOT call sub.unsubscribe(). The push subscription
            // is per-browser, not per-(user, account). Killing it here
            // would silence pushes for every other user / account on this
            // device. Just clear the per-pair opt-in flag so the current
            // pair's toggle reads OFF; refresh() respects the flag.
            setOptedIn(userId, accountId, false);
            // Per-user IDB mirror: only flip to false if NO sales account
            // for this user remains opted-in. Otherwise the SW would stop
            // adding the multi-user label even though the user still gets
            // pushes for other accounts.
            void idbSetUserOptedIn(userId, anyAccountOptedIn(userId));
            // Drop the (user, account) pair from the SW re-register set.
            void idbRemoveOptedInPair(userId, accountId);
            setState(prev => ({...prev, subscription: null, optedIn: false, isSubscribing: false}));
        } catch (err) {
            setState(prev => ({...prev, isSubscribing: false, error: (err as Error).message || 'disable-failed'}));
        }
    }, [userId, accountId]);

    return {state, enable, disable, refresh};
}
